import {
  englishTranslations,
  spanishTranslations,
  type TranslationKey,
} from './translations';
import type { Locale } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import {
  updateCurrentUserLanguage,
} from '@/api/authApi';
import { getToken } from '@/utils/cookies';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type Language = 'en' | 'es';

type TranslationValues = Record<string, string | number>;

type LanguageContextValue = {
  language: Language;
  dateLocale: Locale;
  setLanguage: (language: Language) => void;
  setLanguageFromServer: (language: Language) => void;
  t: (key: TranslationKey, values?: TranslationValues) => string;
};

const LANGUAGE_STORAGE_KEY = 'nptlabs_language';
const dictionaries = {
  en: englishTranslations,
  es: spanishTranslations,
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

function getInitialLanguage(): Language {
  const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return storedLanguage === 'es' ? 'es' : 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguageFromServer = useCallback((nextLanguage: Language) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
  }, []);

  const setLanguage = useCallback((nextLanguage: Language) => {
    setLanguageFromServer(nextLanguage);

    if (getToken()) {
      void updateCurrentUserLanguage(nextLanguage).catch((error) => {
        console.error('Failed to save user language:', error);
      });
    }
  }, [setLanguageFromServer]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback(
    (key: TranslationKey, values?: TranslationValues) => {
      const template = dictionaries[language][key] ?? englishTranslations[key];

      if (!values) {
        return template;
      }

      return Object.entries(values).reduce(
        (result, [name, value]) =>
          result.split(`{{${name}}}`).join(String(value)),
        template as string
      );
    },
    [language]
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      dateLocale: language === 'es' ? es : enUS,
      setLanguage,
      setLanguageFromServer,
      t,
    }),
    [language, setLanguage, setLanguageFromServer, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }

  return context;
}
